import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { AuthenticationService } from '../shared/services/authentication.service';
import { LoginRequest } from '../shared/models/user';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroying$ = new Subject<void>();
  loginStatus = false;
  isLoading = false;
  errorText = '';

  constructor(
    private auth: AuthenticationService,
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  ngOnInit(): void {
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (!result) {
          // No redirect result; do nothing until user clicks login
        }
      },
      error: () => {}
    });

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED))
      .subscribe(() => {
        this.setLoginDisplay();
      });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None), takeUntil(this.destroying$))
      .subscribe(() => {
        this.setLoginDisplay();
      });
  }

  onLogin() {
    this.errorText = '';
    this.isLoading = true;
    this.auth.loginSso();
  }

  private setLoginDisplay() {
    this.loginStatus = this.msalService.instance.getAllAccounts().length > 0;
    if (this.loginStatus) {
      this.isLoading = true;
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        this.isLoading = false;
        return;
      }
      const account = accounts[0];
      this.msalService.instance.setActiveAccount(account);
      const tokenRequest = { scopes: ['user.read'], account };
      this.msalService.acquireTokenSilent(tokenRequest as any).subscribe({
        next: (tokenResponse) => {
          if (!tokenResponse?.accessToken || !tokenResponse?.idToken) {
            this.isLoading = false;
            return;
          }
          const expiry = new Date((new Date()).getTime() + 3600 * 1000);
          // Store MSAL tokens in sessionStorage (standard format). Not sent as HTTP headers, avoids 8KB limit.
          sessionStorage.setItem(
            'msal-access-token',
            JSON.stringify({ token: tokenResponse.accessToken, expiry })
          );
          sessionStorage.setItem(
            'msal-id-token',
            JSON.stringify({ token: tokenResponse.idToken, expiry })
          );
          sessionStorage.setItem('current-username', account?.username ?? '');
          sessionStorage.setItem('current-email', account?.username ?? '');
          const req: LoginRequest = {
            accessToken: tokenResponse.accessToken,
            idToken: tokenResponse.idToken,
            email: account?.username ?? ''
          };
          this.auth.login(req).subscribe({
            next: () => this.router.navigate(['/screen','DASHBOARD']),
            error: (err) => {
              this.isLoading = false;
              this.errorText = this.getErrorMessage(err);
            }
          });
        },
        error: (err) => {
          this.isLoading = false;
          this.errorText = this.getErrorMessage(err);
        }
      });
    }
  }

  private getErrorMessage(err: any): string {
    if (!err) return 'Sign-in failed. Please try again.';
    if (typeof err === 'string') return err;
    if (err.error?.message) return err.error.message;
    if (err.message) return err.message;
    return 'Sign-in failed. Please try again.';
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}

